import { useUserContext } from "@contexts/UserContext/index.context";
import { Box, Typography } from "@mui/material";
import { formatAppDate } from "@repo/utils/date";
import * as Sentry from "@sentry/react";
import { UserRole } from "@types_def/models/user.type";
import type { AdminRank } from "@utils/constants/adminRanks";
import { adminRanks } from "@utils/constants/adminRanks";
import { useEffect, useState } from "react";
import { useFindAdminBySubQuery, useFindClientBySubQuery } from "@/libs/generated/hooks";

/** Header clock, e.g. "dimanche 25 octobre - 14h28". */
const formatHeaderDate = (date: Date) => formatAppDate(date, "EEEE d MMMM - HH'h'mm");

function DashboardSubHeader() {
  const { userState } = useUserContext();
  const [date, setDate] = useState(() => formatHeaderDate(new Date()));
  const userSub = userState?.sub ?? "";
  const isAdminRole = userState?.["custom:role"] === UserRole.ADMIN;

  const { data: adminData, error: adminError } = useFindAdminBySubQuery(
    {
      GetBySubInput: { sub: userSub },
    },
    {
      enabled: userSub.length > 0 && isAdminRole,
      select: ({ findAdminBySub }) => findAdminBySub,
    }
  );

  const { data: clientData, error: clientError } = useFindClientBySubQuery(
    {
      GetBySubInput: { sub: userSub },
    },
    {
      enabled: userSub.length > 0 && !isAdminRole,
      select: ({ findClientBySub }) => findClientBySub,
    }
  );

  const username = isAdminRole
    ? (adminData?.firstName ?? "")
    : (clientData?.assigneeFirstName ?? "");

  const adminRankValue = typeof adminData?.rank === "number" ? adminData.rank : undefined;
  const adminRank: AdminRank | undefined =
    isAdminRole && adminRankValue !== undefined
      ? adminRanks.find((c) => c.rank === adminRankValue)
      : undefined;

  useEffect(() => {
    const timer = setInterval(() => {
      setDate(formatHeaderDate(new Date()));
    }, 10 * 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const queryError: unknown = adminError ?? clientError;
    if (queryError !== null && queryError !== undefined) {
      Sentry.captureException(queryError, {
        extra: {
          component: "DashboardSubHeader",
          userState,
          queryError,
        },
      });
    }
  }, [adminError, clientError, userState]);

  return (
    <Box
      component="section"
      sx={{
        p: "1rem",
        backgroundColor: "rgba(0, 36, 79, 0.1)", // #00244f1a
        display: "grid",
        gridTemplateColumns: "1fr",
        alignItems: "center",
        gap: "1rem",
        height: "max-content",
        "&.dashboard .user-info": {
          fontWeight: 700,
        },
      }}
      className="dashboard"
    >
      <Box
        className="title"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "0.6rem",
          fontWeight: 600,
        }}
      >
        {/* Optional role-based logo */}
        {/* {role === UserRole.CLIENT.toLowerCase() && (
          <img src="/images/arval-logo.webp" alt="Arval Logo" />
        )} */}
        <Typography
          component="span"
          sx={{
            fontWeight: "bold",
            fontSize: "2rem",
            fontFamily: "Georgia, serif",
          }}
        >
          {username}
        </Typography>

        {adminRank !== undefined && (
          <Box sx={{ display: "flex", alignItems: "center", ml: 0.6 }}>
            <Box
              component="img"
              src={adminRank.logo}
              alt={`${adminRank.text} logo`}
              sx={{
                width: 25,
                verticalAlign: "middle",
                display: "inline-block",
              }}
            />
            <Typography
              component="span"
              sx={{
                ml: 0.3,
                fontSize: 20,
                verticalAlign: "text-top",
                color: "#646464",
              }}
            >
              {adminRank.text}
            </Typography>
          </Box>
        )}
      </Box>
      <Typography sx={{ fontWeight: 700 }}>{date}</Typography>
    </Box>
  );
}

export default DashboardSubHeader;
